import { MultiSelectOption } from '@dispatch/shared/types';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import React from 'react';
import {
  LEADING_FORM_CONTROL_LABEL,
  LEADING_FORM_CONTROL_LABEL_MARGIN,
} from './leadingFormControlLabelMargin';

const ACTIONS_ROW_SX: SxProps<Theme> = [
  LEADING_FORM_CONTROL_LABEL_MARGIN,
  { padding: '10px', [LEADING_FORM_CONTROL_LABEL]: { marginLeft: 0 } },
];

export const ReasonModal = ({
  onSubmit,
  onClose,
  modalState,
  setModalState,
  reasonOptions,
  title = 'In order to reschedule, you must select a reason for the change',
  inputLabel = 'Reschedule Reason',
  onCancel,
  needForceReassign,
}: {
  onSubmit: (reason: string, forceReassign?: boolean) => void;
  onClose: () => void;
  modalState: {
    open: boolean;
    callback?: (e: any) => void;
  };
  setModalState?: React.Dispatch<{
    open: boolean;
    callback?: (e: any) => void;
  }>;
  reasonOptions?: MultiSelectOption[];
  title?: string;
  inputLabel?: string;
  onCancel?: () => void;
  needForceReassign?: boolean;
}) => {
  const [reasonOption, setReasonOption] = React.useState<string | null>();
  const [forceReassign, setForceReassign] = React.useState<boolean>(false);
  const options = reasonOptions?.map((reason) => {
    return (
      <MenuItem value={reason.value} key={reason.value}>
        {reason.title}
      </MenuItem>
    );
  });

  return (
    <Modal open={modalState?.open || false}>
      <Stack
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          padding: 0,
          boxShadow: 1,
          borderRadius: 1,
          border: 'none',
        }}
      >
        <Stack sx={{ padding: 2, mt: 3 }}>
          <Typography
            component="h1"
            sx={{ fontSize: '20px', fontWeight: '700' }}
            data-testid={'confirm-modal-title'}
          >
            {title}
          </Typography>
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <InputLabel id="reason-select">{inputLabel}</InputLabel>
            <Select
              labelId="reason-select"
              id="reason-select"
              value={reasonOption}
              label="Reschedule Reason"
              onChange={(value) => {
                setReasonOption(value.target.value);
              }}
            >
              {options}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1} sx={ACTIONS_ROW_SX}>
          {needForceReassign && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={forceReassign}
                  onChange={(value) => setForceReassign(value.target.checked)}
                />
              }
              label="Force Reassign"
            />
          )}
          <Button
            variant="text"
            color="info"
            sx={{
              fontWeight: '700',
              lineHeight: '1.3750em',
              letterSpacing: '0.029em',
              marginLeft: 'auto !important',
            }}
            onClick={() => {
              setModalState?.({ open: false });
              setReasonOption(null);
              setForceReassign(false);
              onClose();
              onCancel?.();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            color="info"
            sx={{
              fontWeight: '700',
              lineHeight: '1.3750em',
              letterSpacing: '0.029em',
            }}
            onClick={() => {
              onSubmit(reasonOption ?? '', forceReassign);
              setReasonOption(null);
              setForceReassign(false);
            }}
          >
            Confirm
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
